import { MultiStepModal, useMultiStepModal } from '@alltrails/modal';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import { ComponentProps, useEffect } from 'react';
import ProductsAvailable from '@alltrails/amplitude/enums/ProductsAvailable';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import CheckoutModalStep from '../../CheckoutModalStep/CheckoutModalStep';
import CheckoutWelcome from '../../CheckoutWelcome/CheckoutWelcome';
import styles from './BaseUpsellModal.module.scss';
import BaseUpsellStep from './BaseUpsellStep';

type Props = {
  children?: React.ReactNode;
  headerImage?: ComponentProps<typeof BaseUpsellStep>['headerImage'];
  heading: React.ReactNode;
  isOpen: boolean;
  isFocusPaused?: boolean;
  onCloseRequest: () => void;
  onSuccess?: () => void;
  primaryButtonText?: React.ReactNode;
  promoEligibleHeading: React.ReactNode;
  tallerOnMobile?: boolean;
  upsellTrigger: ComponentProps<typeof BaseUpsellStep>['upsellTrigger'];
  purchaseTier: PurchaseTier;
  productsAvailable: ProductsAvailable;
  skipCheckout?: boolean;
};

export default function BaseUpsellModal({
  heading,
  promoEligibleHeading,
  headerImage,
  isOpen,
  isFocusPaused = false,
  onCloseRequest,
  onSuccess,
  primaryButtonText,
  children,
  tallerOnMobile = false,
  upsellTrigger,
  purchaseTier,
  productsAvailable,
  skipCheckout = false
}: Props) {
  const { activeModal, goBack, navigateTo } = useMultiStepModal<'upsell' | 'checkout' | 'welcome'>('upsell');
  const isApplePaySDKEnabled = useExperiments()['web-growth-rm-apple-pay-sdk']?.value === 'treatment';

  const sendExposure = useExposureEvent('growth-rm-price-testing-2025');

  useEffect(() => {
    if (isOpen) {
      sendExposure();
    }
  }, [isOpen, sendExposure]);

  return (
    <MultiStepModal
      isFullHeight={activeModal === 'checkout'}
      backgroundClassName={styles.containerOverOsano}
      initialFocus={false}
      isOpen={isOpen}
      isPaused={isFocusPaused || (isApplePaySDKEnabled && activeModal === 'checkout')}
      onCloseRequest={onCloseRequest}
      focusTrapOptions={{ clickOutsideDeactivates: false }}
      {...(activeModal !== 'welcome' ? { onBackRequest: goBack } : {})}
      {...(tallerOnMobile || activeModal === 'checkout' || activeModal === 'welcome' ? { modalClassName: styles.taller } : {})}
    >
      {activeModal === 'checkout' && (
        <CheckoutModalStep
          navigateToWelcome={() => navigateTo('welcome')}
          closeModal={onCloseRequest}
          onSuccess={onSuccess}
          purchaseTier={purchaseTier}
          trigger={upsellTrigger}
        />
      )}
      {activeModal === 'upsell' && (
        <BaseUpsellStep
          heading={heading}
          headerImage={headerImage}
          promoEligibleHeading={promoEligibleHeading}
          navigateTo={navigateTo}
          onCloseRequest={onCloseRequest}
          onSuccess={onSuccess}
          primaryButtonText={primaryButtonText}
          upsellTrigger={upsellTrigger}
          productType={purchaseTier === 'peak' ? 'peak' : 'plus'}
          productsAvailable={productsAvailable}
          skipCheckout={skipCheckout}
        >
          {children}
        </BaseUpsellStep>
      )}
      {activeModal === 'welcome' && <CheckoutWelcome closeModal={onCloseRequest} purchaseTier={purchaseTier} />}
    </MultiStepModal>
  );
}
